import React, {
    Dispatch,
    SetStateAction,
    useEffect,
    useMemo,
    useState,
    useRef,
    ReactNode,
} from 'react';
import {
    useTable,
    useBlockLayout,
    Cell,
    HeaderGroup,
    useSortBy,
    useRowState,
    useExpanded,
    Row,
    UseRowStateLocalState,
} from 'react-table';
import _isEqual from 'lodash/isEqual';
import _get from 'lodash/get';
import _sumBy from 'lodash/sumBy';
import * as naturalCompare from 'string-natural-compare';
import { useQuery } from '@apollo/client';

import {
    CompactNumberCell,
    DollarsCellCompact,
    NameCell,
    RowExpanderCell,
    TableHeader,
    TableHeaderGroup,
} from '@trqk/components';
import { getStatementListLabel } from '@trqk/utils';
import { queryStrings } from '@trqk/resolvers';

import { columnWidths, defaultColumnWidth, rowHeight } from '../../constants';
import { EntityNameCell, StatementRow } from './components';

const { GET_UNVIEWED_STATEMENTS } = queryStrings;

/**
 * @typedef {ManageStatementsTableProps}
 * @property {ReactNode} children
 * @property {number} height height for the table
 * @property {string[]} newStatementIds array of statementIds that finished uploading since the
 * ManageStatements modal was opened or have not been selected for viewing in the statement selector
 * @property {ActiveEntity[]} rowData array of ActiveEntity objects; each of these
 * is an aggregation of all the statementView objects for a given entity. This allows us to "roll up"
 * the statement views as child rows of each entity within the table.
 * @property {Dispatch<SetStateAction<string[]>>} setSelectedStatementIds setState function to update
 * the selectedStatementIds in the ManageStatements modal
 * @property {string[]} selectedStatementIds array of selected statement IDs from the ManageStatements
 * modal
 * @property {Maybe<number>} resetRowSelection a timestamp that will reset the row selection when
 * it is updated in the parent
 */
interface ManageStatementsTableProps {
    children?: ReactNode;
    height: number;
    rowData: ActiveEntity[];
    setSelectedStatementIds: Dispatch<SetStateAction<string[]>>;
    selectedStatementIds: string[];
    resetRowSelection: Maybe<number>;
}

/* eslint-disable react/jsx-props-no-spreading */

/**
 * Renders a table of statement data, as well as the upload progress for each currently uploading
 * statement.
 *
 * The total of all column widths should equal the `contentWidth` variable in '../constants.ts'.
 *
 * @param {ManageStatementsTableProps} props
 */
export const ManageStatementsTable = ({
    children,
    height,
    resetRowSelection,
    setSelectedStatementIds,
    rowData,
}: ManageStatementsTableProps): JSX.Element => {
    /* BEGIN UI state logic */

    const [data, setData] = useState(rowData);

    const skipPageResetRef = useRef(false);

    useEffect((): void => {
        if (!_isEqual(rowData, data)) {
            // When data gets updated with this function, set a flag
            // to disable all of the auto resetting
            skipPageResetRef.current = true;
            setData(rowData);
        }
    }, [rowData, data, setData]);

    /* END UI state logic */

    /* BEGIN table management logic */

    const defaultColumn = useMemo(
        () => ({
            width: defaultColumnWidth,
        }),
        [],
    );

    const columns = useMemo(
        () => [
            {
                Header: (): null => null,
                id: 'expander',
                Cell: RowExpanderCell,
                width: columnWidths.expander,
            },
            {
                id: 'name',
                accessor: (activeEntity: ActiveEntity): ActiveEntity =>
                    activeEntity,
                sortType: (
                    rowA: Row<ActiveEntity>,
                    rowB: Row<ActiveEntity>,
                ): number =>
                    naturalCompare(
                        rowA.original.entity.name,
                        rowB.original.entity.name,
                    ),
                Cell: EntityNameCell,
                headerLabel: 'Entity',
                Header: TableHeader,
                width: columnWidths.name + columnWidths.nameIndent,
            },
            {
                id: 'statements',
                accessor: ({ completedStatementViews }: ActiveEntity): number =>
                    completedStatementViews.length,
                Cell: CompactNumberCell,
                headerLabel: 'Statements',
                Header: TableHeader,
                width: columnWidths.statements,
            },
            {
                id: 'pro',
                accessor: ({ entity }: ActiveEntity): Maybe<string> =>
                    entity?.pro ?? null,
                headerLabel: 'PRO',
                Header: TableHeader,
                width: columnWidths.pro,
            },
            {
                id: 'role_type',
                accessor: ({ entity }: ActiveEntity): string => {
                    const roleType = entity?.role_type ?? null;

                    if (roleType === null || roleType === 'unknown') {
                        return '';
                    }

                    return roleType;
                },
                Cell: NameCell,
                headerLabel: 'Role',
                Header: TableHeader,
                width: columnWidths.role_type,
            },
            {
                id: 'period',
                accessor: ({ completedStatementViews }: ActiveEntity): string =>
                    getStatementListLabel(completedStatementViews, '-', false),
                headerLabel: 'Distribution Period',
                Header: TableHeader,
                disableSortBy: true,
                width: columnWidths.period,
            },
            {
                id: 'earnings',
                accessor: ({ completedStatementViews }: ActiveEntity): number =>
                    _sumBy(completedStatementViews, 'earnings'),
                Cell: DollarsCellCompact,
                headerLabel: 'Earnings',
                Header: TableHeader,
                disableSortBy: true,
                width: columnWidths.earnings,
            },
            {
                id: 'spacer',
                accessor: (): null => null,
                width: columnWidths.spacer,
                Cell: NameCell,
            },
        ],
        // eslint will say this is an unnecessary dependency, but it helps keep the values in the
        // table up to date
        // eslint-disable-next-line
        [rowData],
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        state: { rowState },
        setRowState,
    } = useTable<ActiveEntity>(
        {
            columns,
            data,
            defaultColumn,
            initialState: {
                sortBy: [
                    {
                        id: 'name',
                        desc: false,
                    },
                ],
            },
            autoResetRowState: !skipPageResetRef.current,
            autoResetExpanded: !skipPageResetRef.current,
            initialRowStateAccessor:
                (): UseRowStateLocalState<ManageStatementsRowState> => ({
                    selectedStatementIds: [],
                }),
        },
        useBlockLayout,
        useSortBy,
        useRowState,
        useExpanded,
    );

    // When the resetRowSelection prop changes, toggle all rows to not selected
    useEffect((): void => {
        if (resetRowSelection !== null) {
            rows.forEach(({ id }: Row<ActiveEntity>): void => {
                setRowState([id], {
                    cellState: {},
                    selectedStatementIds: [],
                });
            });
        }
    }, [resetRowSelection, setRowState, rows]);

    /* END table management logic */

    const rowStatesSelectedStatementIds = Object.values(rowState).reduce(
        (acc: string[], currentRowState: Record<string, unknown>): string[] => {
            // It doesn't look like it's possible to set the type for the individual state
            // object on each row, so I'm disabling eslint errors here.
            // eslint-disable-next-line
            // @ts-ignore
            const rowSelectedStatementIds: string[] = _get(
                currentRowState,
                ['selectedStatementIds'],
                [],
            );

            return [...acc, ...rowSelectedStatementIds];
        },
        [] as string[],
    );

    const selectedStatementIdsString = JSON.stringify(
        rowStatesSelectedStatementIds,
    );

    // Set selectedStatementIds in ManageStatements when rowStatesSelectedStatementIds changes
    useEffect(() => {
        setSelectedStatementIds(JSON.parse(selectedStatementIdsString));
    }, [selectedStatementIdsString, setSelectedStatementIds]);

    const unviewedStatementsQuery = useQuery(GET_UNVIEWED_STATEMENTS);
    const unviewedStatements: string[] =
        unviewedStatementsQuery?.data?.uiState?.statements
            ?.unviewedStatements ?? [];

    const renderedRows = useMemo(
        () =>
            rows.map((row, index) => {
                const ariaRowIndex = index + 1;

                prepareRow(row);

                const rowProps = row.getRowProps();

                const { key: rowKey, role, style } = rowProps;

                return (
                    <div key={rowKey}>
                        <div
                            role={role}
                            style={{
                                ...style,
                                height: rowHeight,
                            }}
                            aria-rowindex={ariaRowIndex}
                            className="tr relative flex items-center border-b border-tertiary-200"
                        >
                            {row.cells.map((cell: Cell<ActiveEntity>) => {
                                const { key: cellKey, ...restOfCellProps } =
                                    cell.getCellProps();

                                return (
                                    <div
                                        key={cellKey}
                                        {...restOfCellProps}
                                        className="td text-sm font-medium"
                                    >
                                        {cell.render('Cell')}
                                    </div>
                                );
                            })}
                        </div>
                        {row.isExpanded && (
                            <div
                                className="border-b border-tertiary-200"
                                style={{ width: style?.width }}
                            >
                                {row.original.completedStatementViews.map(
                                    (
                                        statementView: TrqkStatementView,
                                        statementRowIndex: number,
                                    ): JSX.Element => {
                                        const statementId =
                                            statementView?.statement_id ?? null;

                                        const selected =
                                            statementId !== null &&
                                            rowStatesSelectedStatementIds.includes(
                                                statementId,
                                            );

                                        return (
                                            <StatementRow
                                                key={
                                                    statementId ??
                                                    `statement.${index}`
                                                }
                                                index={statementRowIndex}
                                                isNew={
                                                    statementId !== null &&
                                                    unviewedStatements.includes(
                                                        statementId,
                                                    )
                                                }
                                                roleType={
                                                    row.original.entity
                                                        ?.role_type ?? null
                                                }
                                                selected={selected}
                                                statementView={statementView}
                                                setRowState={row.setState}
                                            />
                                        );
                                    },
                                )}
                            </div>
                        )}
                    </div>
                );
            }),
        [prepareRow, rows, rowStatesSelectedStatementIds, unviewedStatements],
    );

    return (
        <div {...getTableProps()} className="trqk-table font-normal">
            <div>
                {headerGroups.map(
                    (headerGroup: HeaderGroup<ActiveEntity>): JSX.Element => {
                        const { key: headerGroupKey } =
                            headerGroup.getHeaderGroupProps();

                        return (
                            <TableHeaderGroup<ActiveEntity>
                                key={headerGroupKey}
                                headerGroup={headerGroup}
                            />
                        );
                    },
                )}
            </div>
            <div
                {...getTableBodyProps()}
                style={{ maxHeight: height }}
                className="overflow-y-auto overflow-x-hidden respond-to-mouse"
            >
                {children}
                {renderedRows}
            </div>
        </div>
    );
};
